import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, Search, Filter, FileText, FileSpreadsheet, Sparkles, Loader2, AlertCircle, ChevronDown, Briefcase, CheckCircle } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIButton, AIBadge } from '../components/AIComponents';
import { usePagination } from '../hooks/usePagination';
import { CreateJobFromQuoteModal } from '../components/LineageComponents';

// Quote Life sub-navigation (Estimates & Proposals inside Quote Life)
const quoteSubNav = [
  { path: '/quotes', label: 'Quotes', icon: ClipboardList },
  { path: '/quotes/estimates', label: 'Estimates', icon: FileText },
  { path: '/quotes/proposals', label: 'Proposals', icon: FileSpreadsheet },
];

export function Quotes() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const { 
    data: quotes, 
    loading, 
    loadingMore,
    error, 
    hasMore, 
    loadMore, 
    fetchData,
    pagination
  } = usePagination('quotes', { initialLimit: 25, orgAsQueryParam: true });

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  // Transform quotes for display
  const transformedQuotes = quotes.map(quote => ({
    id: quote.id,
    client: quote.client_name || 'Unknown Client',
    title: quote.job_title || `Quote`,
    amount: quote.quote_amount_gross || quote.quote_amount_net || 0,
    status: quote.status?.toLowerCase() || 'draft',
    date: quote.created_at ? new Date(quote.created_at).toLocaleDateString('en-GB') : 'N/A',
    // Keep raw data for modal
    rawData: quote
  }));

  // Calculate stats
  const pendingCount = transformedQuotes.filter(q => q.status === 'sent' || q.status === 'draft').length;
  const acceptedCount = transformedQuotes.filter(q => q.status === 'accepted').length;
  const declinedCount = transformedQuotes.filter(q => q.status === 'declined').length;

  // Get accepted quotes for the "Create Job" action
  const acceptedQuotes = transformedQuotes.filter(q => q.status === 'accepted');

  const handleCreateJobClick = (quote) => {
    setSelectedQuote(quote.rawData);
    setShowCreateJobModal(true);
  };

  const handleJobCreated = (result) => {
    setSuccessMessage(`Job created successfully! ${result.line_items_created} line items inherited.`);
    setTimeout(() => {
      navigate(`/jobs/${result.job_id}`);
    }, 1500);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Quote Life</h1>
          <p>Manage your quotes, estimates, and proposals</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" disabled>
            <Plus size={16} />
            New Quote
          </button>
        </div>
      </div>

      {/* Sub Navigation - Estimates & Proposals inside Quote Life */}
      <div className="flex gap-sm mb-xl">
        {quoteSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-success-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
          <span style={{ color: 'var(--status-success)' }}>{successMessage}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="responsive-grid-4" style={{ marginBottom: 'var(--space-xl)' }}>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-warning)' }}>
                {pendingCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Awaiting Response</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-success)' }}>
                {acceptedCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Accepted</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-danger)' }}>
                {declinedCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Declined</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                {pagination.total}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Total Quotes</div>
            </GlassPanel>
          </div>

          {/* Accepted Quotes - Create Job CTA */}
          {acceptedQuotes.length > 0 && (
            <GlassPanel 
              title="Ready to Start Work" 
              titleIcon={CheckCircle}
              className="mb-xl"
              style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.1) 100%)' }}
            >
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
                These accepted quotes are ready to become Jobs. Creating a Job will inherit all line items from the quote.
              </p>
              <div className="list-container" data-testid="accepted-quotes-list">
                {acceptedQuotes.map((quote) => (
                  <div 
                    key={quote.id} 
                    className="glass-panel" 
                    style={{ 
                      padding: 'var(--space-md)',
                      marginBottom: 'var(--space-sm)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    data-testid={`accepted-quote-${quote.id}`}
                  >
                    <div>
                      <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{quote.title}</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {quote.client} • £{quote.amount.toFixed(2)}
                      </div>
                    </div>
                    <button 
                      onClick={() => handleCreateJobClick(quote)}
                      className="btn btn-primary"
                      data-testid={`create-job-from-quote-${quote.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                    >
                      <Briefcase size={16} />
                      Create Job
                    </button>
                  </div>
                ))}
              </div>
            </GlassPanel>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
            <div style={{ 
              flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
              padding: 'var(--space-sm) var(--space-md)', background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)'
            }}>
              <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
              <input type="text" placeholder="Search quotes..." style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none' }} />
            </div>
            <button className="btn btn-secondary"><Filter size={16} />Filter</button>
          </div>

          <GlassPanel title="All Quotes" titleIcon={ClipboardList}>
            {transformedQuotes.length > 0 ? (
              <>
                <div className="list-container" data-testid="quotes-list">
                  {transformedQuotes.map((quote) => (
                    <div key={quote.id} className="glass-panel" style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-md)' }} data-testid={`quote-item-${quote.id}`}>
                      <Link to={`/quotes/${quote.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div className="list-item-content">
                            <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                              {quote.title}
                              <AIBadge text="AI" />
                            </div>
                            <div className="list-item-subtitle">{quote.client} • {quote.date}</div>
                          </div>
                          <div className="list-item-meta">
                            <span className={`status-badge ${quote.status === 'accepted' ? 'completed' : quote.status === 'declined' ? 'overdue' : 'pending'}`}>
                              {quote.status}
                            </span>
                            <span className="list-item-amount">£{quote.amount.toFixed(2)}</span>
                          </div>
                        </div>
                      </Link>
                      
                      {/* Actions Row */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginTop: 'var(--space-md)',
                        paddingTop: 'var(--space-md)',
                        borderTop: '1px solid var(--glass-border)'
                      }}>
                        {/* AI Actions */}
                        <div className="quote-card-ai-actions" style={{ flex: 1 }}>
                          <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
                            Generate quote with AI
                          </AIButton>
                        </div>
                        
                        {/* Create Job Button for Accepted Quotes */}
                        {quote.status === 'accepted' && (
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              handleCreateJobClick(quote);
                            }}
                            className="btn btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                          >
                            <Briefcase size={14} />
                            Create Job
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn btn-secondary"
                      data-testid="load-more-quotes"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Load More ({pagination.total - transformedQuotes.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: 'var(--space-md)',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)'
                }}>
                  Showing {transformedQuotes.length} of {pagination.total} quotes
                </div>
              </>
            ) : (
              <EmptyState icon={ClipboardList} title="No quotes yet" description="Create your first quote to get started" />
            )}
          </GlassPanel>
        </>
      )}

      {/* Create Job Modal */}
      <CreateJobFromQuoteModal
        isOpen={showCreateJobModal}
        onClose={() => {
          setShowCreateJobModal(false);
          setSelectedQuote(null);
        }}
        quote={selectedQuote}
        onSuccess={handleJobCreated}
      />
    </div>
  );
}

export default Quotes;
